import { firstYearOnly, knownAnnualCost, reviewedTuitionRow } from "./feeYear";
// Course tuition as reports and lists may show it: never a quarantined figure, always with its scope/basis qualifier.
export const COA_SOURCE = "College Scorecard — cost.attendance.academic_year";

export function isTuitionQuarantined(cost) {
  const c = cost || {};
  return c.evidence?.source === COA_SOURCE || c.tuition_status === "not_yet_verified";
}

export function courseTuitionUsd(degree, field = "tuition_intl_annual_usd") {
  const c = degree?.cost;
  return isTuitionQuarantined(c) ? null : c?.[field] ?? null;
}

const BASIS = {
  us_out_of_state_rate_proxy: "US out-of-state rate, estimate for international students",
  us_out_of_state_rate_proxy_unconfirmed: "US out-of-state rate, year not confirmed",
  same_rate_all_students_per_scorecard: "same rate for all students (College Scorecard)",
};

function courseQualifier(c) {
  const combined = reviewedTuitionRow(c) && c.tuition_includes_required_fees ? "combined tuition and required fees" : null;
  const parts = [BASIS[c.tuition_intl_basis], combined, firstYearOnly(c) ? "first academic year rate only" : null];
  if (c.tuition_source_year_key) parts.push(`Scorecard data year ${c.tuition_source_year_key}`);
  else if (/scorecard/i.test(c.evidence?.source || "")) parts.push("year not confirmed");
  return parts.filter(Boolean).join(" · ") || null;
}

/** {usd, qualifier, incomplete}: course figure first, then a clearly-qualified university-wide figure. */
export function reportTuition(d, u) {
  const c = d?.cost || {};
  const blocked = isTuitionQuarantined(c);
  const course = blocked ? null : (c.tuition_intl_annual_usd || c.tuition_domestic_annual_usd || c.tuition_annual_usd || null);
  // Indicative = anything that is not a course-specific quote from the university/editors (Scorecard years, proxies, fallbacks).
  if (course) return { usd: course, course: true, qualifier: courseQualifier(c), incomplete: false, indicative: /scorecard/i.test(c.evidence?.source || "") || !!c.tuition_intl_basis };
  const uni = u?.tuition_intl_annual_usd || u?.tuition_domestic_annual_usd || u?.fees_annual_usd || null;
  if (uni) {
    const sc = /scorecard/i.test(JSON.stringify(u?.tuition_source || u?.tuition_intl_source || ""));
    return {
      usd: uni,
      qualifier: `University-wide figure, not a course quote${sc ? " · College Scorecard, year not confirmed" : ""}${blocked ? " · course fee not yet verified" : ""}`,
      incomplete: false,
      indicative: true,
    };
  }
  return { usd: null, qualifier: blocked ? "Course tuition not yet verified" : null, incomplete: true, indicative: false };
}

/** Report degree cost: never an all-years total from a first-year rate or an incomplete reviewed reference; the sourced annual figure stays. */
export function reportDegreeCost(d, u, years, living, fmt) {
  const t = reportTuition(d, u);
  const c = d?.cost || {};
  if (t.course && (firstYearOnly(c) || reviewedTuitionRow(c))) {
    const k = knownAnnualCost(c, t.usd);
    if (k.complete) return { total: Math.round((k.annual_total_usd + living) * years), tuition: t, label: t.qualifier };
    const yr = typeof c.tuition_academic_year === "string" ? ` · ${c.tuition_academic_year.replace("-", "/")}` : "";
    const kind = c.tuition_includes_required_fees ? "combined tuition and required fees" : "tuition";
    return { total: null, tuition: t, label: `${fmt(t.usd)} a year${yr} (${kind}) · ${k.incomplete_reason}` };
  }
  return { total: t.usd ? Math.round((t.usd + living) * years) : null, tuition: t, label: t.qualifier };
}

/** Shortlist cost range from verified course totals only; indicative or missing fees are counted, never ranked. */
export function rollupCosts(items) {
  let excluded = 0;
  const costs = [];
  for (const it of items) {
    const d = it.degree;
    const u = it.university;
    if (!d) continue;
    const t = reportTuition(d, u);
    if (!t.usd || t.indicative) { excluded += 1; continue; }
    const yrs = d.duration_years || (u?.country === "US" ? 4 : 3);
    costs.push((t.usd + (u?.accommodation_annual_usd || 0) + (u?.food_annual_usd || 0)) * yrs);
  }
  return { minCost: costs.length ? Math.min(...costs) : null, maxCost: costs.length ? Math.max(...costs) : null, costExcluded: excluded };
}

export function rollupCostLabel(r, fmt) {
  const note = r.costExcluded ? ` (${r.costExcluded} with indicative or unverified fees not included)` : "";
  if (r.minCost) return `${fmt(r.minCost)} — ${fmt(r.maxCost)}${note}`;
  return r.costExcluded ? "Indicative or unverified fees only: see course pages" : "See course pages";
}
